/**
 * Local-development helpers for the two public forms.
 *
 * These forms are long — the loan application is seven steps and gates Continue
 * on each step's required fields — so exercising anything near the end (the
 * review step, the signature, the submit payload, the upload path) means typing
 * the whole thing out again on every reload. This fills it in one click.
 *
 * NOT a product feature and never visible to a borrower: `IS_LOCAL_DEV` gates
 * every consumer, and the values are obvious throwaways ("Dev Tester",
 * example.com, 555 numbers) so a prefilled submission is recognizable as junk if
 * one ever reaches the queue.
 */

/**
 * Matches the dashboard's ReduxStateDevButton test, deliberately: `npm start`
 * sets NODE_ENV=development, and a production bundle smoke-tested from localhost
 * (`serve -s build`) should still expose it.
 *
 * The hostname half is a RUNTIME check, so this module is not dead-code
 * eliminated from a production bundle — the fake values ship with it. That is
 * fine (they're fabricated, not secret) but it does mean this is a
 * render-time-only gate, not a compile-time one.
 */
export const IS_LOCAL_DEV =
  process.env.NODE_ENV === "development" ||
  (typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname));

/** Mirrors RffFileUploadField's newUploadId — see there for why not just uuid. */
let devUploadCounter = 0;
const newDevUploadId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  devUploadCounter += 1;
  return `dev-upload-${devUploadCounter}`;
};

// Smallest valid PNG — a 1x1 transparent pixel. Real bytes rather than an empty
// Blob so the upload is a genuine image all the way through: the backend sniffs
// content type, and a zero-byte file would fail for reasons that have nothing to
// do with whatever is being tested.
const ONE_PX_PNG_B64 =
  "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

/**
 * One `uploadMetadata` entry, shaped exactly as RffFileUploadField builds it —
 * including the `id`, which is what the backend matches each binary part against
 * to find its metadata (lendmarq-os app/models/loan_files.py:40). An entry
 * without one uploads but silently loses its docGroup/docType.
 */
export const makeDevUpload = ({
  fileName = "dev-photo-id.png",
  docGroup = null,
  docType = null,
} = {}) => {
  const binary = atob(ONE_PX_PNG_B64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const file = new File([bytes], fileName, { type: "image/png" });
  return {
    id: newDevUploadId(),
    file,
    name: fileName,
    file_display_name: fileName,
    docGroup,
    docType,
  };
};

/**
 * A signature as the trimmed PNG data URL RffSignatureField produces, drawn
 * rather than stubbed with a blank pixel — the review step and the submission
 * PDF both render this image, and an invisible one makes them look broken when
 * the thing being tested is the layout around it.
 *
 * Falls back to the 1px PNG if canvas is unavailable (jsdom, a locked-down
 * browser), since an unsignable form can't reach submit at all.
 */
export const makeDevSignature = (name = "Dev Tester") => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 420;
    canvas.height = 110;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context");
    ctx.fillStyle = "#111827";
    // Caveat is loaded in public/index.html for the Type tab; if it hasn't
    // arrived yet the fallback cursive still draws something legible.
    ctx.font = '52px "Caveat", cursive';
    ctx.textBaseline = "middle";
    ctx.fillText(name, 14, 58);
    return canvas.toDataURL("image/png");
  } catch (e) {
    return `data:image/png;base64,${ONE_PX_PNG_B64}`;
  }
};

/**
 * A structured address in the shape RffAddressEditingField writes. The parts
 * matter individually — the credit pull matches on streetNumber / streetName /
 * city / state / zip — so this can't be collapsed to `fullAddress` alone.
 */
export const devAddress = ({
  streetNumber = "1600",
  streetName = "Pennsylvania Avenue NW",
  city = "Washington",
  state = "DC",
  zip = "20500",
} = {}) => ({
  fullAddress: `${streetNumber} ${streetName}, ${city}, ${state} ${zip}`,
  streetNumber,
  streetName,
  unit: null,
  city,
  state,
  zip,
  county: null,
  country: "United States",
  countryCode: "US",
  latitude: null,
  longitude: null,
});
